import '@mcp-b/global';
import React, { useContext, useEffect } from 'react';
import { useWebMCP, type InferOutput } from '@mcp-b/react-webmcp';
import { EditorStoreContext } from 'adflow';
import { MCP_B_RELAY_EMBED_SCRIPT_ID, MCP_B_RELAY_EMBED_SRC } from './constants';
import { useTemplateWebMCPFeature } from './hooks/useTemplateWebMCPFeature';
import { logTemplateWebMCP } from './logging';
import {
  executeTemplateBuilderRead,
  TEMPLATE_BUILDER_READ_TOOL,
} from './tools/templateBuilderRead';

type Props = {
  templateId: string;
  editorReady: boolean;
  hasEditPermissions: boolean;
};

type ReadToolOutput = InferOutput<typeof TEMPLATE_BUILDER_READ_TOOL.outputSchema>;

function useGatedRelayEmbed(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) {
      return;
    }

    let script = document.getElementById(MCP_B_RELAY_EMBED_SCRIPT_ID) as HTMLScriptElement | null;
    const created = !script;
    if (!script) {
      script = document.createElement('script');
      script.id = MCP_B_RELAY_EMBED_SCRIPT_ID;
      script.src = MCP_B_RELAY_EMBED_SRC;
      script.async = true;
      document.head.appendChild(script);
    }

    return () => {
      if (created) {
        script?.remove();
      }
    };
  }, [enabled]);
}

function TemplateWebMCPTools({
  templateId,
  hasEditPermissions,
}: {
  templateId: string;
  hasEditPermissions: boolean;
}) {
  const editorContext = useContext(EditorStoreContext);

  useEffect(() => {
    logTemplateWebMCP('register', { tool: TEMPLATE_BUILDER_READ_TOOL.name });
    return () => {
      logTemplateWebMCP('unregister', { tool: TEMPLATE_BUILDER_READ_TOOL.name });
    };
  }, []);

  // Every `useWebMCP` field, with what actually reaches a relay listing today:
  // - Relayed: name, description, inputSchema, annotations
  // - Registered in-page only until the embed prefers `listTools()`: outputSchema
  // - Hook-local (never listed): enabled, execute
  useWebMCP({
    name: TEMPLATE_BUILDER_READ_TOOL.name,
    description: TEMPLATE_BUILDER_READ_TOOL.description,
    enabled: TEMPLATE_BUILDER_READ_TOOL.enabled,
    inputSchema: TEMPLATE_BUILDER_READ_TOOL.inputSchema,
    outputSchema: TEMPLATE_BUILDER_READ_TOOL.outputSchema,
    annotations: TEMPLATE_BUILDER_READ_TOOL.annotations,
    // `execute` is typed as returning the bare payload, which the polyfill would then wrap
    // itself. We return a full tool result instead, because `normalizeToolResponse` passes
    // through any object carrying a `content` array and that is the only way to keep our
    // human-readable first content block and `isError`. The cast records that gap.
    execute: (input: unknown) =>
      executeTemplateBuilderRead({
        store: editorContext.store,
        templateId,
        canEdit: hasEditPermissions,
        input,
      }) as unknown as Promise<ReadToolOutput>,
  });

  return null;
}

function TemplateWebMCPHost({ templateId, editorReady, hasEditPermissions }: Props) {
  const featureEnabled = useTemplateWebMCPFeature();
  const shouldRegister = featureEnabled && editorReady && hasEditPermissions;
  useGatedRelayEmbed(shouldRegister);

  if (!shouldRegister) {
    return null;
  }

  return <TemplateWebMCPTools templateId={templateId} hasEditPermissions={hasEditPermissions} />;
}

export default TemplateWebMCPHost;
